import { useCallback, useEffect, useState, type CSSProperties } from "react";
import type { CanonicalGame, GameMechanic } from "@hanjan-shieo/content";
import { advanceBouncingTurn, arcadeRuntimeMode, createBouncingState, resetBouncingStreak, resolveRoundResult, type BouncingCommand, type BouncingMove, type RoundResult, type RoundVerdict } from "@hanjan-shieo/domain";
import { Button } from "@hanjan-shieo/ui";
import { audioBus } from "./audio";
import { haptic } from "./platform";
import { AuthenticGameEngine } from "./GameEngines";
import { GameRuntimeContext, useGameRuntime } from "./GameRuntimeContext";
import { authenticGuide } from "./GameGuide";

const mechanicLabel: Record<GameMechanic, string> = {
  word_race: "말 잇기",
  quiz_choice: "선택 퀴즈",
  reflex: "순간 반응",
  memory_chain: "기억 연쇄",
  social_vote: "동시 투표",
  deduction: "추리 토론",
  bluff_strategy: "블러핑",
  chance_reveal: "운 공개",
  dexterity: "손기술",
  observation: "관찰 빙고",
  performance: "표현 미션",
  team_score: "팀 대결",
  hosted_round: "진행자 라운드",
};

function BouncingBallEngine({ finish }: { finish: (verdict: RoundVerdict, evidence: string) => void }) {
  const runtime = useGameRuntime();
  const [state, setState] = useState(() => createBouncingState(runtime.partySize));
  const [lastMove, setLastMove] = useState<BouncingMove | null>(null);
  const [resetNotice, setResetNotice] = useState(false);
  const complete = state.streak >= 10;
  const directionLabel = state.direction === 1 ? "시계 방향" : "반시계 방향";
  const seatPoint = (seat: number) => {
    const angle = -Math.PI / 2 + ((seat - 1) * Math.PI * 2) / state.playerCount;
    return { x: 160 + Math.cos(angle) * 124, y: 160 + Math.sin(angle) * 124 };
  };
  const passPath = lastMove ? (() => {
    const from = seatPoint(lastMove.fromSeat); const to = seatPoint(lastMove.toSeat);
    const curve = lastMove.command === "BOUNCE" ? 38 : 70;
    const midX = (from.x + to.x) / 2; const midY = (from.y + to.y) / 2;
    const dx = midX - 160; const dy = midY - 160;
    const length = Math.hypot(dx, dy) || 1;
    return `M ${from.x} ${from.y} Q ${midX - (dx / length) * curve} ${midY - (dy / length) * curve} ${to.x} ${to.y}`;
  })() : null;
  const moveDetail = lastMove?.command === "WHIZ"
    ? `공이 바로 ${lastMove.toSeat}번에게 갔어요.`
    : lastMove?.command === "BOUNCE"
      ? `${lastMove.skippedSeat}번을 건너 공이 ${lastMove.toSeat}번에게 갔어요.`
      : lastMove
        ? `방향을 ${directionLabel}으로 바꾸고 공이 ${lastMove.toSeat}번에게 갔어요.`
        : "1번이 단어를 외친 뒤, 같은 단어 버튼을 눌러 시작하세요.";

  const command = useCallback((word: BouncingCommand) => {
    if (complete) return;
    const turn = advanceBouncingTurn(state, word);
    setState(turn.state);
    setLastMove(turn.move);
    setResetNotice(false);
    audioBus.play(word === "WHIZ" ? "whiz" : word === "BOUNCE" ? "bounce" : "boing");
    haptic(turn.state.streak >= 10 ? "success" : "light");
  }, [complete, state]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.isComposing) return;
      const target = event.target as HTMLElement | null;
      if (target?.matches("input, select, textarea, [contenteditable='true']")) return;
      const word = event.key === "1" ? "WHIZ" : event.key === "2" ? "BOUNCE" : event.key === "3" ? "BOING" : null;
      if (word) { event.preventDefault(); command(word); }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [command]);

  function changePlayers(delta: number) {
    const playerCount = Math.min(8, Math.max(3, state.playerCount + delta));
    if (runtime.fixedParty || playerCount === state.playerCount) return;
    setState(createBouncingState(playerCount));
    setLastMove(null);
    setResetNotice(false);
    audioBus.play("toggle");
  }

  function resetRally() {
    setState((current) => resetBouncingStreak(current));
    setLastMove(null);
    setResetNotice(true);
    audioBus.play("error");
  }

  return <div className="bouncing-engine">
    <div className="bouncing-toolbar">
      <div className="player-stepper" aria-label={`함께 하는 사람 ${state.playerCount}명`}>
        <button aria-label="인원 한 명 줄이기" disabled={state.playerCount === 3} onClick={() => changePlayers(-1)}>−</button>
        <span>함께 하는 사람<strong>{state.playerCount}명</strong></span>
        <button aria-label="인원 한 명 늘리기" disabled={state.playerCount === 8} onClick={() => changePlayers(1)}>＋</button>
      </div>
      <span className="direction-label"><i aria-hidden="true">{state.direction === 1 ? "↻" : "↺"}</i>{directionLabel}</span>
    </div>

    <div className="bouncing-board">
      <div className="seat-ring" aria-label={`${state.playerCount}명 중 지금 외칠 사람 ${state.currentSeat}번, ${directionLabel}`}>
        <svg className="pass-map" viewBox="0 0 320 320" aria-hidden="true">
          <defs><marker id="bouncing-arrow" markerWidth="8" markerHeight="8" refX="5" refY="3" orient="auto"><path d="M0,0 L0,6 L6,3 z" /></marker></defs>
          <circle className="direction-track" cx="160" cy="160" r="116" />
          {passPath && <path key={`${state.streak}-${lastMove?.command}`} className={`pass-route pass-route--${lastMove?.command.toLowerCase()}`} d={passPath} markerEnd="url(#bouncing-arrow)" />}
        </svg>
        {Array.from({ length: state.playerCount }, (_, index) => {
          const seat = index + 1; const point = seatPoint(seat);
          const style = { "--seat-x": `${(point.x / 320) * 100}%`, "--seat-y": `${(point.y / 320) * 100}%` } as CSSProperties;
          return <span key={seat} style={style} className={`${seat === state.currentSeat ? "active" : ""} ${seat === lastMove?.fromSeat ? "from" : ""}`}><b>{seat}번</b>{seat === state.currentSeat && <small>내 차례</small>}</span>;
        })}
        <div className="turn-card"><span>지금 외칠 사람</span><strong>{state.currentSeat}번</strong><small>말한 뒤 같은 버튼을 눌러요</small></div>
      </div>
      <div className={`command-feedback ${lastMove ? `command-feedback--${lastMove.command.toLowerCase()}` : ""}`} aria-live="polite" aria-atomic="true">
        <span>{lastMove ? `${lastMove.fromSeat}번이 외쳤어요` : "첫 차례"}</span>
        <strong>{lastMove ? `${lastMove.command}!` : "1번부터 시작"}</strong>
        <p>{resetNotice ? `현재 ${state.currentSeat}번부터 이어가기 기록만 다시 시작해요.` : moveDetail}</p>
      </div>
    </div>

    <div className="rally-progress">
      <div><span>함께 이어가기</span><strong>{state.streak}<small>/10</small></strong></div>
      <div className="rally-pips" role="progressbar" aria-label="열 번 이어가기" aria-valuemin={0} aria-valuemax={10} aria-valuenow={Math.min(state.streak, 10)}>{Array.from({ length: 10 }, (_, index) => <i key={index} className={index < state.streak ? "done" : ""}>{index + 1}</i>)}</div>
      <p>{complete ? "열 번 이어졌어요. 모두 함께 성공했습니다!" : state.streak === 0 ? "틀리지 않고 열 번 이어지면 모두 성공해요." : `${10 - state.streak}번만 더 이어 보세요.`}</p>
    </div>

    <div className="chant-controls" aria-label="가상 공 보내기">
      <button className="chant-whiz" disabled={complete} aria-keyshortcuts="1" aria-label="WHIZ 위즈, 바로 옆 사람에게 보내기, 단축키 1" onClick={() => command("WHIZ")}><span className="command-symbol" aria-hidden="true">→</span><b>WHIZ <small>위즈</small></b><span>옆 사람에게</span><kbd>1</kbd></button>
      <button className="chant-bounce" disabled={complete} aria-keyshortcuts="2" aria-label="BOUNCE 바운스, 한 사람 건너 보내기, 단축키 2" onClick={() => command("BOUNCE")}><span className="command-symbol" aria-hidden="true">⇢</span><b>BOUNCE <small>바운스</small></b><span>한 사람 건너</span><kbd>2</kbd></button>
      <button className="chant-boing" disabled={complete} aria-keyshortcuts="3" aria-label="BOING 보잉, 방향을 바꾸고 옆 사람에게 보내기, 단축키 3" onClick={() => command("BOING")}><span className="command-symbol" aria-hidden="true">↺</span><b>BOING <small>보잉</small></b><span>방향 바꾸고 옆으로</span><kbd>3</kbd></button>
    </div>
    {complete
      ? <div className="bouncing-complete"><span aria-hidden="true">✓</span><div><b>열 번 이어가기 성공!</b><small>실제 플레이 기록으로 결과를 남겨요.</small></div><Button onClick={() => finish("success", `가상 공 ${state.streak}회 연속 패스`)}>성공 기록 보기</Button></div>
      : <div className="rally-recovery"><button className="chain-reset" onClick={resetRally}>순서가 헷갈렸어요</button><small>현재 차례는 그대로 두고, 이어가기 숫자만 0으로 돌려요.</small></div>}
  </div>;
}

export function GameStage({ game, onComplete, onPass, partySize = 4, active = true, localParty = false }: { game: CanonicalGame; onComplete: (result: RoundResult) => void; onPass: () => void; partySize?: number; active?: boolean; localParty?: boolean }) {
  const [visible, setVisible] = useState(() => !document.hidden);
  useEffect(() => { const update = () => setVisible(!document.hidden); addEventListener("visibilitychange", update); return () => removeEventListener("visibilitychange", update); }, []);
  const finish = (verdict: RoundVerdict, evidence: string) => verdict === "pass" ? onPass() : onComplete(resolveRoundResult(verdict, evidence));
  const mechanic = game.gameplay.mechanic;
  const runtimeMode = arcadeRuntimeMode(game);
  if (runtimeMode !== "authentic_interactive") return <div className="game-unavailable"><strong>이 게임은 아직 플레이할 수 없어요.</strong><p>고유 조작과 승패가 완성된 뒤 공개하겠습니다.</p><button className="stage-pass" onClick={onPass}>실제 게임 목록으로 돌아가기</button></div>;
  const activeInstruction = authenticGuide[game.canonicalId]?.steps[1] ?? game.gameplay.firstAction;
  return <GameRuntimeContext.Provider value={{ partySize, fixedParty: true, active: active && visible, localParty }}><div className="game-stage"><header className="stage-contract"><div className="stage-meta"><span>{mechanicLabel[mechanic]}</span><span>화면에서 직접 플레이</span></div><small>이번 도전</small><h2>{game.gameplay.goal}</h2><dl><div><dt>지금 할 일</dt><dd>{activeInstruction}</dd></div><div><dt>성공하면</dt><dd>{game.gameplay.reward}</dd></div></dl></header><div className="engine-shell" data-mechanic={mechanic} data-runtime={runtimeMode}>
    {game.canonicalId === "bouncing-ball-chant" ? <BouncingBallEngine finish={finish} /> : <AuthenticGameEngine game={game} finish={finish} />}
  </div><button className="stage-pass" onClick={onPass}>이 게임 건너뛰기 <span>· 불이익 없음</span></button></div></GameRuntimeContext.Provider>;
}
